import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEventManagementService, useEventPolling } from '../services/eventManagementService';
import type { LiveEvent, EventFilters, EventsResponse, RowEventTarget } from '../../../types/frontend';

export const eventKeys = {
  list: (customerId: string, filters: EventFilters, search: string) =>
    ['event-management', 'events', customerId, filters, search] as const,
};

const EVENT_CAP = 100;
// The window read around a target event's time, either side.
const TARGET_WINDOW_MS = 30_000;

interface EventExplorerResult {
  events: LiveEvent[];
  sources: string[];
  detailTypes: string[];
  regions: string[];
  accounts: string[];
  loading: boolean;
  error: string | null;
  isAutoRefreshing: boolean;
  isCustomRange: boolean;
  filters: EventFilters;
  setFilters: React.Dispatch<React.SetStateAction<EventFilters>>;
  setError: (value: string | null) => void;
  toggleAutoRefresh: () => void;
  loadEvents: () => Promise<void>;
  activeSearch: string;
  submitSearch: (term: string) => void;
  /** The target event once its read found it; null before, and when it was not found. */
  selectedTargetEvent: LiveEvent | null;
  /** The target's read returned without it: it is past the event log's look-back. */
  targetMissing: boolean;
  dismissTargetMissing: () => void;
}

interface TargetOutcome {
  event: LiveEvent | null;
  missing: boolean;
}

const NO_TARGET: TargetOutcome = { event: null, missing: false };

export function useEventExplorer(
  customerId: string,
  onEventCountChange: (count: number) => void,
  /** An event to open, such as a dataset row's source event: read around its time and selected once found. */
  target?: RowEventTarget | null,
  /** Called once the target has been taken, so a later visit starts from the usual window. */
  onTargetTaken?: () => void,
): EventExplorerResult {
  const eventService = useEventManagementService();
  const queryClient = useQueryClient();

  const [localError, setLocalError] = useState<string | null>(null);
  // Track the error the user has dismissed (by its query timestamp) so setError(null)
  // actually clears a load-failure banner — a fresh failure (new errorUpdatedAt) re-shows.
  const [dismissedErrorAt, setDismissedErrorAt] = useState(0);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [filters, setFilters] = useState<EventFilters>({
    timeRange: '1h',
    source: '',
    detailType: '',
    region: '',
    account: '',
  });

  // activeSearch is the SUBMITTED term that drives the fetch. The Logs Insights
  // query is slow, so search runs on explicit submit, not on every keystroke.
  const [activeSearch, setActiveSearch] = useState('');
  const submitSearch = useCallback((term: string) => setActiveSearch(term.trim()), []);

  const fetchFilters = useMemo<EventFilters>(
    () => ({ ...filters, search: activeSearch || undefined }),
    [filters, activeSearch],
  );

  const query = useQuery({
    queryKey: eventKeys.list(customerId, filters, activeSearch),
    queryFn: () => eventService.getEvents(customerId, fetchFilters),
    enabled: !!customerId,
    // Changing filters/search keeps the current rows visible until the (slow)
    // query returns instead of flashing empty.
    placeholderData: keepPreviousData,
  });

  const events = useMemo(() => query.data?.events ?? [], [query.data]);

  // The id of a target event still to find once its read returns.
  const [pendingTargetId, setPendingTargetId] = useState<string | null>(null);
  const [targetOutcome, setTargetOutcome] = useState<TargetOutcome>(NO_TARGET);

  useEffect(() => {
    if (!target) return;
    const time = Date.parse(target.time);
    setFilters((prev) => ({
      ...prev,
      timeRange: undefined,
      startTime: new Date(time - TARGET_WINDOW_MS).toISOString(),
      endTime: new Date(time + TARGET_WINDOW_MS).toISOString(),
    }));
    setActiveSearch(target.id);
    setTargetOutcome(NO_TARGET);
    setPendingTargetId(target.id);
    onTargetTaken?.();
  }, [target, onTargetTaken]);

  useEffect(() => {
    if (!pendingTargetId || activeSearch !== pendingTargetId || query.isFetching) return;
    // A failed read says nothing about where the event is; the load error says what happened.
    if (query.isError) {
      setPendingTargetId(null);
      return;
    }
    if (query.isPlaceholderData) return;
    const found = events.find((e) => e.id === pendingTargetId) ?? null;
    setTargetOutcome({ event: found, missing: !found });
    setPendingTargetId(null);
  }, [pendingTargetId, activeSearch, query.isFetching, query.isError, query.isPlaceholderData, events]);
  // Stable, so the memoised InlineAlert it closes does not rerender on every Explorer render.
  const dismissTargetMissing = useCallback(() => setTargetOutcome((prev) => ({ ...prev, missing: false })), []);
  const facets = query.data?.facets;
  const { sources, detailTypes, regions, accounts } = useMemo(() => ({
    sources: facets?.sources ?? [],
    detailTypes: facets?.detailTypes ?? [],
    regions: facets?.regions ?? [],
    accounts: facets?.accounts ?? [],
  }), [facets]);

  // The badge tracks query.data.total: a base load sets the server total, the live
  // tail writes its client count into the same field, so this one effect reports both.
  useEffect(() => {
    const total = query.data?.total;
    if (total !== undefined) onEventCountChange(total);
  }, [query.data?.total, onEventCountChange]);

  const queryErrorMessage = query.isError && query.errorUpdatedAt !== dismissedErrorAt
    ? 'Failed to load events. Please try again.'
    : null;
  const error = localError ?? queryErrorMessage;
  const setError = useCallback((value: string | null) => {
    if (value === null) setDismissedErrorAt(query.errorUpdatedAt);
    setLocalError(value);
  }, [query.errorUpdatedAt]);

  // The live poller prepends into the SAME cached list; keep refs so the merge
  // targets the current query key without re-creating the callback each render.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const searchRef = useRef(activeSearch);
  searchRef.current = activeSearch;

  const handleNewEvents = useCallback((newEvents: LiveEvent[]) => {
    const key = eventKeys.list(customerId, filtersRef.current, searchRef.current);
    const prev = queryClient.getQueryData<EventsResponse>(key);
    const seen = new Set<string>();
    const merged = [...newEvents, ...(prev?.events ?? [])].filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    }).slice(0, EVENT_CAP);
    // Always write: seed the entry if the base load hasn't populated the cache yet
    // (polling during the slow first fetch) so the tick shows immediately — the base
    // fetch overwrites it when it resolves, and its own result carries recent events.
    // The client count goes into total so the single count effect reports it.
    queryClient.setQueryData<EventsResponse>(key, {
      ...prev,
      events: merged,
      total: merged.length,
      facets: prev?.facets ?? { sources: [], detailTypes: [], regions: [], accounts: [] },
      hasMore: prev?.hasMore ?? false,
    });
  }, [queryClient, customerId]);

  const loadEvents = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: eventKeys.list(customerId, filtersRef.current, searchRef.current) });
  }, [queryClient, customerId]);

  const isCustomRange = !filters.timeRange;

  const { startPolling, stopPolling } = useEventPolling(customerId, handleNewEvents, 10000, filters);

  const autoRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toggleAutoRefresh = useCallback(() => {
    if (isCustomRange) return;
    const AUTO_REFRESH_MAX_DURATION_MS = 10 * 60 * 1000;
    if (isAutoRefreshing) {
      stopPolling();
      if (autoRefreshTimeoutRef.current) {
        clearTimeout(autoRefreshTimeoutRef.current);
        autoRefreshTimeoutRef.current = null;
      }
    } else {
      startPolling();
      autoRefreshTimeoutRef.current = setTimeout(() => {
        stopPolling();
        setIsAutoRefreshing(false);
        autoRefreshTimeoutRef.current = null;
      }, AUTO_REFRESH_MAX_DURATION_MS);
    }
    setIsAutoRefreshing(!isAutoRefreshing);
  }, [isCustomRange, isAutoRefreshing, startPolling, stopPolling]);

  // Stop auto-refresh polling when a custom range is active
  useEffect(() => {
    if (isCustomRange && isAutoRefreshing) {
      stopPolling();
      if (autoRefreshTimeoutRef.current) {
        clearTimeout(autoRefreshTimeoutRef.current);
        autoRefreshTimeoutRef.current = null;
      }
      setIsAutoRefreshing(false);
    }
  }, [isCustomRange, isAutoRefreshing, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
      if (autoRefreshTimeoutRef.current) {
        clearTimeout(autoRefreshTimeoutRef.current);
      }
    };
  }, [stopPolling]);

  return {
    events,
    sources,
    detailTypes,
    regions,
    accounts,
    loading: query.isFetching,
    error,
    isAutoRefreshing,
    isCustomRange,
    filters,
    setFilters,
    setError,
    toggleAutoRefresh,
    loadEvents,
    activeSearch,
    submitSearch,
    selectedTargetEvent: targetOutcome.event,
    targetMissing: targetOutcome.missing && !query.isError,
    dismissTargetMissing,
  };
}
